import { getAlgoliaQueryIDFromLocalStorage } from 'lib/algolia/v2/helpers'
import { algoliaEventsApi } from 'apis/algolia'
import { convertFromCentsToDollars } from 'components/FormatPrice/helpers'
import { PolaroidSpecificPageProps } from './Polaroid'
import { capitalizeString } from 'lib/helpers'

type UsePolaroidEventsResponseType = {
  handleArtworkClick: (ev: React.SyntheticEvent<Record<string, any>>) => void
}

type UsePolaroidEventsParams = Pick<
  PolaroidSpecificPageProps,
  'algoliaIndice' | 'algoliaUserToken' | 'dataList' | 'dataListId' | 'dataListName'
> & {
  artworkId: number
  artworkTitle: string
  artistName: string
  category: string
  listPrice: number
  sku: string
  positionID: number
  userID: number
  isPrintForDataLayerEvent?: boolean
  shouldSendAlgoliaEvents?: boolean
}

export const usePolaroidEvents = ({
  artworkId,
  artworkTitle,
  artistName,
  category,
  listPrice,
  sku,
  algoliaIndice,
  algoliaUserToken,
  dataList,
  dataListId,
  dataListName,
  positionID,
  userID,
  isPrintForDataLayerEvent,
  shouldSendAlgoliaEvents = true,
}: UsePolaroidEventsParams): UsePolaroidEventsResponseType => {
  const algoliaQueryIdFromLocalStorage = getAlgoliaQueryIDFromLocalStorage()

  const handleArtworkClick = (ev: React.SyntheticEvent<Record<string, any>>) => {
    // Delete this dataLayer event in July as part of universal analytics sunset
    sku &&
      window.dataLayer.push({
        event: 'productClick',
        ecommerce: {
          click: {
            actionField: {
              list: dataList, // feature-results
            },
            products: [
              {
                id: sku, // Product sku
                position: positionID, // Location in the results
                name: artworkTitle, // artwork title
              },
            ],
          },
        },
      })
    // This is the new dataLayer event for GA4
    // Clear the previous ecommerce object.
    window.dataLayer.push({
      ecommerce: null,
    })
    sku &&
      window.dataLayer.push({
        event: 'select_item',
        ecommerce: {
          item_list_id: dataListId,
          item_list_name: capitalizeString(dataListName),
          items: [
            {
              item_category: category.replace(/s$/, '').replace(/\sM/, 'm'),
              item_id: sku || '',
              item_art_id: artworkId || '',
              item_name: artworkTitle || '',
              item_brand: `${artistName}` || '',
              item_variant: isPrintForDataLayerEvent ? 'open' : 'original',
              index: positionID,
              item_list_id: dataListId,
              item_list_name: capitalizeString(dataListName),
              affiliation: 'Online Store',
              price: convertFromCentsToDollars(listPrice) || 0,
              quantity: 1,
            },
          ],
        },
      })
    // Algolia event
    if (shouldSendAlgoliaEvents && sku) {
      algoliaEventsApi({
        events: [
          {
            eventName: 'select_item',
            eventType: 'click',
            index: algoliaIndice,
            userToken: algoliaUserToken,
            authenticatedUserToken: String(userID) || '',
            objectIDs: [String(artworkId)],
            positions: [positionID + 1],
            ...(algoliaQueryIdFromLocalStorage && {
              queryID: algoliaQueryIdFromLocalStorage,
            }), // pass queryID only if its present, otherwise do not pass
          },
        ],
      })
    }
  }

  return { handleArtworkClick }
}
